import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Drawer,
  EmptyState,
  FilterBar,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { IconButton } from '../../ui/components/Button';
import { useT } from '../../i18n';
import { suppliersApi, statusMeta, SUPPLIER_STATUSES } from './suppliersApi';
import { CURRENCY_OPTIONS } from '../settings/settingsApi';
import { supplierPath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import SupplierForm from './SupplierForm';
import './suppliers.css';

const PAGE_SIZE = 25;

const FILTER_FIELDS = [
  { id: 'statuses', label: 'Status', type: 'multiselect', options: SUPPLIER_STATUSES.map((s) => ({ value: s.value, label: s.label })) },
  { id: 'currencies', label: 'Currency', type: 'multiselect', searchable: true, options: CURRENCY_OPTIONS },
  { id: 'category', label: 'Category', type: 'text' },
  { id: 'preferred', label: 'Preferred', type: 'select', options: [{ value: 'true', label: 'Preferred only' }, { value: 'false', label: 'Not preferred' }] },
  { id: 'terms', label: 'Payment terms (days)', type: 'numberrange', minParam: 'termsMin', maxParam: 'termsMax' },
  { id: 'lead', label: 'Lead time (days)', type: 'numberrange', minParam: 'leadMin', maxParam: 'leadMax' },
];

export default function SuppliersPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const t = useT();

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [page, setPage] = useState(1);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const debounceRef = useRef(null);

  const filterParams = useMemo(() => buildFilterParams(FILTER_FIELDS, values), [values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const filterFields = useMemo(
    () => FILTER_FIELDS.map((f) => ({
      ...f,
      label: t(f.label),
      options: f.options?.map((o) => ({ ...o, label: t(o.label) })),
    })),
    [t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await suppliersApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams });
      setData(res);
    } catch (err) {
      setError(err?.message || t('Failed to load suppliers.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(value);
    }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const openProfile = (supplier) => navigate(supplierPath(supplier));

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    if (submitting) return;
    setDrawerOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const created = await suppliersApi.create(payload);
      toast.success(t('Supplier created'));
      setDrawerOpen(false);
      setEditing(null);
      if (created?.id) navigate(supplierPath(created));
      else await load();
    } catch (err) {
      toast.error(t('Could not create supplier'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await suppliersApi.remove(deleteTarget.id);
      toast.success(t('Supplier deleted'));
      setDeleteTarget(null);
      // If we deleted the last row on a page, step back a page.
      if (data.items.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (err) {
      toast.error(t('Could not delete supplier'), { description: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: t('Name'),
      width: 240,
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          {row.code && <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{row.code}</div>}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('Status'),
      width: 120,
      render: (row) => {
        const m = statusMeta(row.status);
        return <Badge tone={m.tone} dot>{t(m.label)}</Badge>;
      },
    },
    {
      key: 'email',
      header: t('Email'),
      width: 220,
      render: (row) => row.email || <span className="pp-cell-muted">—</span>,
    },
    {
      key: 'phone',
      header: t('Phone'),
      width: 150,
      render: (row) => row.phone || <span className="pp-cell-muted">—</span>,
    },
    {
      key: 'currency',
      header: t('Currency'),
      width: 100,
      render: (row) => row.currency,
    },
    {
      key: 'paymentTermsDays',
      header: t('Terms'),
      width: 100,
      align: 'right',
      render: (row) => (row.paymentTermsDays != null ? t('{days}d', { days: row.paymentTermsDays }) : <span className="pp-cell-muted">—</span>),
    },
    {
      key: 'actions',
      header: '',
      width: 90,
      align: 'right',
      resizable: false,
      render: (row) => (
        <div className="pp-row-actions" onClick={(e) => e.stopPropagation()}>
          <IconButton aria-label={t('Open supplier')} variant="ghost" size="icon-sm" onClick={() => openProfile(row)}>
            <Icon.ArrowRight size={15} />
          </IconButton>
          <IconButton aria-label={t('Delete supplier')} variant="ghost" size="icon-sm" onClick={() => setDeleteTarget(row)}>
            <Icon.Trash size={15} />
          </IconButton>
        </div>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Suppliers')}
        description={t('Manage your vendors, contacts and payment terms.')}
        icon={<Icon.Building size={22} />}
        actions={
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>
            {t('New supplier')}
          </Button>
        }
      />

      <FilterBar
        searchPlaceholder={t('Search by name, code or email…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={filterFields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load suppliers")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}>
          <Spinner size={26} />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(row) => row.id}
            density="comfortable"
            onRowClick={openProfile}
            emptyState={
              <EmptyState
                icon={<Icon.Building size={28} />}
                title={hasFilters ? t('No matching suppliers') : t('No suppliers yet')}
                description={
                  hasFilters
                    ? t('Try adjusting your search or filters.')
                    : t('Add your first supplier to start purchasing.')
                }
                action={
                  !hasFilters ? (
                    <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={openCreate}>
                      {t('New supplier')}
                    </Button>
                  ) : null
                }
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination
                page={page}
                pageCount={totalPages}
                onPageChange={setPage}
                totalItems={data.totalCount}
                pageSize={PAGE_SIZE}
              />
            </div>
          )}
        </>
      )}

      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={t('New supplier')}
        description={t('Add a vendor to your organization. You can complete the full profile next.')}
        size="md"
      >
        <SupplierForm
          supplier={editing}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={closeDrawer}
        />
      </Drawer>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDelete}
        title={t('Delete supplier')}
        description={
          deleteTarget
            ? t('Delete "{name}"? This action cannot be undone.', { name: deleteTarget.name })
            : ''
        }
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
      />
    </PageContainer>
  );
}
